import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import NewTicketModal from "../../components/NewTicketModal";
import {
  DevicesFold
} from "@mui/icons-material";
import MoreVertIcon from '@material-ui/icons/MoreVert';
import FlowBuilderModal from "../../components/FlowBuilderModal";
import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  InputAdornment,
  TextField,
  Chip,
  Divider,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import { SocketContext } from "../../context/Socket/SocketContext";
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from '@material-ui/icons/Delete';

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    return action.payload;
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex(c => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex(c => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    background: "#f8fafc",
    minHeight: "calc(100vh - 100px)",
  },
  header: {
    background: "#fff",
    borderRadius: "12px",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a237e",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    "& svg": {
      color: "#1a237e",
      fontSize: "1.8rem",
    }
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  searchField: {
    flex: 1,
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "transparent",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      }
    }
  },
  addButton: {
    background: "#1a237e",
    color: "#fff",
    borderRadius: "8px",
    padding: "0 24px",
    height: "48px",
    fontSize: "0.875rem",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 2px 4px rgba(26,35,126,0.2)",
    "&:hover": {
      background: "#0d47a1",
      boxShadow: "0 4px 8px rgba(26,35,126,0.3)",
    }
  },
  flowsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  flowCard: {
    background: "#fff",
    borderRadius: "10px",
    overflow: "hidden",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    border: "1px solid #e2e8f0",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    }
  },
  flowHeader: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #f1f5f9",
  },
  flowName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1e293b",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& svg": {
      color: "#1a237e",
      fontSize: "1.2rem",
    }
  },
  flowContent: {
    padding: theme.spacing(2),
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "0.75rem",
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  statusActive: {
    background: "#dcfce7",
    color: "#166534",
  },
  statusInactive: {
    background: "#f1f5f9",
    color: "#475569",
  },
  flowActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  actionButton: {
    flex: 1,
    minWidth: "40px",
    height: "36px",
    borderRadius: "6px",
    padding: 0,
    "&:hover": {
      transform: "translateY(-1px)",
    }
  },
  editButton: {
    color: "#1e40af",
    background: "#dbeafe",
    "&:hover": {
      background: "#bfdbfe",
    }
  },
  duplicateButton: {
    color: "#7e22ce",
    background: "#f3e8ff",
    "&:hover": {
      background: "#e9d5ff",
    }
  },
  deleteButton: {
    color: "#be123c",
    background: "#ffe4e6",
    "&:hover": {
      background: "#fecdd3",
    }
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    "& .MuiPaginationItem-root": {
      borderRadius: "6px",
    }
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    background: "#fff",
    borderRadius: "12px",
    gridColumn: "1 / -1",
    border: "1px solid #e2e8f0",
    "& svg": {
      fontSize: "3rem",
      color: "#94a3b8",
      marginBottom: theme.spacing(2),
    }
  },
  emptyStateText: {
    color: "#64748b",
    marginBottom: theme.spacing(2),
    fontSize: "0.875rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    gridColumn: "1 / -1",
  }
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredWebhooks, setFilteredWebhooks] = useState([]);
  const [menuWebhook, setMenuWebhook] = useState(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (searchParam) {
      const filtered = webhooks.filter(webhook =>
        webhook.name.toLowerCase().includes(searchParam.toLowerCase())
      );
      setFilteredWebhooks(filtered);
    } else {
      setFilteredWebhooks(webhooks);
    }
  }, [searchParam, webhooks]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setTotalPages(Math.ceil(data.count / 20));
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    socket.on(`company-${user.companyId}-contact`, data => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      // Cleanup function
    };
  }, [socketManager, user.companyId]);

  const handleSearch = event => {
    setSearchParam(event.target.value);
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setSelectedWebhookName(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setSelectedWebhookName(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = ticket => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (webhook) => {
    setSelectedContactId(webhook.id);
    setSelectedWebhookName(webhook.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async webhookId => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setDeletingContact(null);
      setReloadData(old => !old);
      toast.success("Flujo eliminado con éxito");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async flowId => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId });
      setDeletingContact(null);
      setReloadData(old => !old);
      toast.success("Flujo duplicado con éxito");
    } catch (err) {
      toastError(err);
    }
  };

  const handleMenuOpen = (event, webhook) => {
    setAnchorEl(event.currentTarget);
    setMenuWebhook(webhook);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuWebhook(null);
  };

  const handleEditFlow = (webhook) => {
    history.push(`/flowbuilder/${webhook.id}`);
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  const handleRowClick = (webhookId) => {
    history.push(`/flowbuilder/${webhookId}`);
  };

  const itemsPerPage = 10;
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedWebhooks = filteredWebhooks.slice(startIndex, endIndex);

  return (
    <Box className={classes.root}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={handleCloseOrOpenTicket}
      />

      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData(old => !old)}
      />

      <ConfirmationModal
        title={deletingContact ? `Eliminar flujo ${deletingContact.name}?` : ""}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deletingContact && handleDeleteWebhook(deletingContact.id)}
      >
        ¿Está seguro de que desea eliminar este flujo? Todas las integraciones relacionadas se perderán.
      </ConfirmationModal>

      <ConfirmationModal
        title={deletingContact ? `Duplicar flujo ${deletingContact.name}?` : ""}
        open={confirmDuplicateOpen}
        onClose={() => setConfirmDuplicateOpen(false)}
        onConfirm={() => deletingContact && handleDuplicateFlow(deletingContact.id)}
      >
        ¿Está seguro de que desea duplicar este flujo?
      </ConfirmationModal>

      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.titleContainer}>
            <Typography className={classes.title}>
              <DevicesFold />
              Administrador de Flujos
            </Typography>
            <Typography className={classes.subtitle}>
              Administre sus flujos de conversación automatizados
            </Typography>
          </div>
          <Button
            variant="contained"
            onClick={handleOpenContactModal}
            className={classes.addButton}
            startIcon={<AddIcon />}
          >
            Nuevo Flujo
          </Button>
        </div>
      </div>

      <div className={classes.searchContainer}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar flujos..."
          value={searchParam}
          onChange={handleSearch}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.flowsGrid}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} thickness={4} sx={{ color: "#1a237e" }} />
          </div>
        ) : displayedWebhooks.length > 0 ? (
          displayedWebhooks.map((webhook) => (
            <Card key={webhook.id} className={classes.flowCard}>
              <div className={classes.flowHeader}>
                <Typography className={classes.flowName}>
                  <DevicesFold />
                  {webhook.name}
                </Typography>
              </div>
              
              <div className={classes.flowContent}>
                <div className={`${classes.statusBadge} ${
                  webhook.active ? classes.statusActive : classes.statusInactive
                }`}>
                  {webhook.active ? "Activo" : "Inactivo"}
                </div>

                <div className={classes.flowActions}>
                  <Button
                    className={`${classes.actionButton} ${classes.editButton}`}
                    onClick={() => handleEditFlow(webhook)}
                    title="Editar flujo"
                  >
                    <EditIcon fontSize="small" />
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.duplicateButton}`}
                    onClick={() => {
                      setDeletingContact(webhook);
                      setConfirmDuplicateOpen(true);
                    }}
                    title="Duplicar flujo"
                  >
                    <FileCopyIcon fontSize="small" />
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.deleteButton}`}
                    onClick={() => {
                      setDeletingContact(webhook);
                      setConfirmOpen(true);
                    }}
                    title="Eliminar flujo"
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <div className={classes.emptyState}>
            <DevicesFold />
            <Typography className={classes.emptyStateText}>
              {searchParam
                ? `Ningún flujo encontrado para "${searchParam}"`
                : "Ningún flujo registrado aún"}
            </Typography>
            <Button
              variant="contained"
              onClick={handleOpenContactModal}
              className={classes.addButton}
              startIcon={<AddIcon />}
            >
              Crear Flujo
            </Button>
          </div>
        )}
      </div>

      {filteredWebhooks.length > 0 && (
        <div className={classes.pagination}>
          <Pagination
            count={Math.ceil(filteredWebhooks.length / itemsPerPage)}
            page={pageNumber}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            showFirstButton
            showLastButton
          />
        </div>
      )}
    </Box>
  );
};

export default FlowBuilder;